import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, WhatsappLogo, Gavel, Clock, MusicNote, At } from "@phosphor-icons/react";

const EXP_LABEL = {
  pro: "Profesional",
  passionate: "Apasionado",
  lover: "Amante",
};

const STATUS_BADGE = {
  pending: { label: "Pendiente", cls: "bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/30" },
  accepted: { label: "Aceptado", cls: "bg-[#B4F34A]/15 text-[#B4F34A] border-[#B4F34A]/30" },
  rejected: { label: "Rechazado", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

function buildWhatsAppLink(phone, name, status) {
  // phone arrives as +52XXXXXXXXXX → strip non digits
  const clean = (phone || "").replace(/\D/g, "");
  const message = status === "accepted"
    ? `Hola ${name}, soy del equipo de Tu MusiKa. ¡Felicidades! Fuiste seleccionado(a) como Juez Oficial para evaluar las mejores canciones del mes. En breve te compartiremos los detalles para empezar.`
    : `Hola ${name}, soy del equipo de Tu MusiKa. Gracias por postularte como juez. Queremos darte una actualización sobre tu postulación.`;
  return `https://wa.me/${clean}?text=${encodeURIComponent(message)}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function JudgesTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [stats, setStats] = useState({ pending: 0, accepted: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(false);

  const load = (status = filter) => {
    setLoading(true);
    Promise.all([
      api.get("/admin/judges", { params: status ? { status } : {} }),
      api.get("/admin/judges/stats"),
    ])
      .then(([list, st]) => {
        setItems(list.data.items || []);
        setStats(st.data || {});
      })
      .catch(() => toast.error("No se pudo cargar las postulaciones"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); }, [filter]);

  const changeStatus = (id, status) => {
    api.post(`/admin/judges/${id}/status`, { status })
      .then(() => {
        toast.success(status === "accepted" ? "Postulación aceptada" : status === "rejected" ? "Postulación rechazada" : "Marcada como pendiente");
        load(filter);
      })
      .catch(() => toast.error("Error actualizando estado"));
  };

  return (
    <div className="space-y-4" data-testid="judges-tab">
      {/* Stats pills */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
        <button
          onClick={() => setFilter("pending")}
          data-testid="judges-filter-pending"
          className={`rounded-xl p-3 text-left border transition-all ${filter === "pending" ? "bg-[#FFB800]/10 border-[#FFB800]" : "bg-[#121212] border-white/5 hover:border-white/15"}`}
        >
          <p className="text-xs uppercase tracking-widest text-white/50">Pendientes</p>
          <p className="text-2xl font-display font-bold text-[#FFB800]">{stats.pending || 0}</p>
        </button>
        <button
          onClick={() => setFilter("accepted")}
          data-testid="judges-filter-accepted"
          className={`rounded-xl p-3 text-left border transition-all ${filter === "accepted" ? "bg-[#B4F34A]/10 border-[#B4F34A]" : "bg-[#121212] border-white/5 hover:border-white/15"}`}
        >
          <p className="text-xs uppercase tracking-widest text-white/50">Aceptados</p>
          <p className="text-2xl font-display font-bold text-[#B4F34A]">{stats.accepted || 0}</p>
        </button>
        <button
          onClick={() => setFilter("rejected")}
          data-testid="judges-filter-rejected"
          className={`rounded-xl p-3 text-left border transition-all ${filter === "rejected" ? "bg-red-500/10 border-red-500" : "bg-[#121212] border-white/5 hover:border-white/15"}`}
        >
          <p className="text-xs uppercase tracking-widest text-white/50">Rechazados</p>
          <p className="text-2xl font-display font-bold text-red-400">{stats.rejected || 0}</p>
        </button>
        <button
          onClick={() => setFilter("")}
          data-testid="judges-filter-all"
          className={`rounded-xl p-3 text-left border transition-all ${filter === "" ? "bg-white/10 border-white/40" : "bg-[#121212] border-white/5 hover:border-white/15"}`}
        >
          <p className="text-xs uppercase tracking-widest text-white/50">Total</p>
          <p className="text-2xl font-display font-bold text-white">{stats.total || 0}</p>
        </button>
      </div>

      {/* List */}
      {loading && <p className="text-white/40 text-center py-8">Cargando...</p>}
      {!loading && items.length === 0 && (
        <p className="text-white/40 text-center py-12" data-testid="judges-empty">
          No hay postulaciones {filter ? `con estado "${filter}"` : ""}.
        </p>
      )}

      {items.map((j) => {
        const badge = STATUS_BADGE[j.status] || STATUS_BADGE.pending;
        return (
          <div key={j.id} className="rounded-2xl bg-[#121212] border border-white/5 p-4 sm:p-5" data-testid={`judge-card-${j.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-[#FFB800]/15 flex items-center justify-center shrink-0">
                  <Gavel weight="fill" size={18} className="text-[#FFB800]" />
                </div>
                <div className="min-w-0">
                  <p className="font-display font-bold text-white truncate">{j.name}</p>
                  <p className="text-xs text-white/50">{j.phone} · {fmtDate(j.created_at)}</p>
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded-full border text-xs font-bold ${badge.cls}`}>{badge.label}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 text-sm">
              <div className="flex items-start gap-2">
                <MusicNote size={14} className="text-white/40 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40">Experiencia</p>
                  <p className="text-white">{EXP_LABEL[j.experience] || j.experience}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock size={14} className="text-white/40 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/40">Tiempo / sem</p>
                  <p className="text-white">{j.available_time}</p>
                </div>
              </div>
              {j.email && (
                <div className="flex items-start gap-2">
                  <At size={14} className="text-white/40 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-widest text-white/40">Email</p>
                    <p className="text-white text-xs truncate">{j.email}</p>
                  </div>
                </div>
              )}
            </div>

            {j.favorite_genres?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs uppercase tracking-widest text-white/40 mb-1.5">Géneros favoritos</p>
                <div className="flex flex-wrap gap-1.5">
                  {j.favorite_genres.map((g) => (
                    <span key={g} className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-white/80 capitalize">
                      {g.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {j.motivation && (
              <div className="mb-4">
                <p className="text-xs uppercase tracking-widest text-white/40 mb-1.5">Motivación</p>
                <p className="text-sm text-white/80 italic">&ldquo;{j.motivation}&rdquo;</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
              {j.status !== "accepted" && (
                <button
                  onClick={() => changeStatus(j.id, "accepted")}
                  data-testid={`judge-accept-${j.id}`}
                  className="px-3 py-2 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black text-xs font-bold flex items-center gap-1.5"
                >
                  <CheckCircle weight="fill" size={14} /> Aceptar
                </button>
              )}
              {j.status !== "rejected" && (
                <button
                  onClick={() => changeStatus(j.id, "rejected")}
                  data-testid={`judge-reject-${j.id}`}
                  className="px-3 py-2 rounded-full bg-white/5 hover:bg-red-500/15 text-white text-xs flex items-center gap-1.5"
                >
                  <XCircle size={14} /> Rechazar
                </button>
              )}
              <a
                href={buildWhatsAppLink(j.phone, j.name, j.status === "accepted" ? "accepted" : "pending")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`judge-whatsapp-${j.id}`}
                className="px-3 py-2 rounded-full bg-[#25D366] hover:bg-[#1FBE5A] text-black text-xs font-bold flex items-center gap-1.5 ml-auto"
              >
                <WhatsappLogo weight="fill" size={14} /> WhatsApp
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );
}
